class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.MAX_CACHE_SIZE = parseInt(this.dataset.maxCacheSize || '50', 10);
    this._abortController = null;
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    this._boundHandlers = {
      onChange: FoxTheme.utils.debounce(this.onChange.bind(this), 300),
      onFocus: this.onFocus.bind(this),
      clear: this.clear.bind(this),
      handleSubmit: this.handleSubmit.bind(this)
    };

    const input = this.input;
    const resetButton = this.resetButton;
    if (input) {
      this.focusElement = input;
      input.addEventListener('input', this._boundHandlers.onChange);
      input.addEventListener('focus', this._boundHandlers.onFocus);
    }
    if (resetButton) {
      resetButton.addEventListener('click', this._boundHandlers.clear);
    }
    this.addEventListener('submit', this._boundHandlers.handleSubmit);

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      this.handleClickOutsideBound = this.handleClickOutside.bind(this);
      document.addEventListener('click', this.handleClickOutsideBound);
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    const input = this.input;
    return input ? input.value.trim() : '';
  }

  normalizeSKU(query) {
    const trimmed = query.trim();
    const MAX_SKU_LENGTH = 18;
    const SKU_LENGTH = Math.min(parseInt(this.dataset.skuLength || '18', 10), MAX_SKU_LENGTH);
    
    if (/^\d+$/.test(trimmed) && trimmed.length < SKU_LENGTH && trimmed.length <= MAX_SKU_LENGTH) {
      return trimmed.padStart(SKU_LENGTH, '0');
    }
    
    if (/^\d+$/.test(trimmed) && trimmed.length > MAX_SKU_LENGTH) {
      console.warn(`SKU query too long (${trimmed.length} chars), searching as-is`);
    }
    
    return trimmed;
  }

  getNormalizedQuery() {
    const query = this.getQuery();
    return this.normalizeSKU(query);
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    const input = this.input;
    if (input) {
      input.value = '';
      input.focus();
    }
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    const normalizedQuery = this.getNormalizedQuery();
    
    url.searchParams.set('q', normalizedQuery);
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    url.searchParams.set('resources[options][fields]', 'title,variants.sku,product_type,variants.title');
    
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    if (responseText) {
      this.renderSearchResults(responseText);
      this.setAttribute('results', '');
    } else {
      console.warn('Cache entry was empty, fetching fresh data');
      this.cachedMap.delete(url);
      this.renderSectionFromFetch(url);
    }
  }

  renderSectionFromFetch(url, retryCount = 0) {
    const MAX_RETRIES = parseInt(this.dataset.maxRetries || '2', 10);
    
    if (this._abortController) {
      this._abortController.abort();
    }
    
    if (this._retryTimeout) {
      clearTimeout(this._retryTimeout);
      this._retryTimeout = null;
    }
    
    this._abortController = new AbortController();
    const currentQuery = this.getQuery();
    const requestId = currentQuery;
    
    this.setAttribute('loading', 'true');
    this.resetButton?.classList.add('btn--loading');
    
    let isRetrying = false;

    fetch(url, {
      signal: this._abortController.signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          if (response.status === 429 && retryCount < MAX_RETRIES) {
            const retryAfter = parseInt(response.headers.get('Retry-After') || '1', 10);
            console.warn(`Rate limited, retrying after ${retryAfter}s... (${retryCount + 1}/${MAX_RETRIES})`);
            isRetrying = true;
            this._retryTimeout = setTimeout(() => {
              if (this.getQuery() === requestId) {
                this.renderSectionFromFetch(url, retryCount + 1);
              }
              this._retryTimeout = null;
            }, retryAfter * 1000);
            return null;
          }
          if (response.status >= 500 && retryCount < MAX_RETRIES) {
            console.warn(`Server error ${response.status}, retrying... (${retryCount + 1}/${MAX_RETRIES})`);
            isRetrying = true;
            this._retryTimeout = setTimeout(() => {
              if (this.getQuery() === requestId) {
                this.renderSectionFromFetch(url, retryCount + 1);
              }
              this._retryTimeout = null;
            }, 1000 * (retryCount + 1));
            return null;
          }
          if (response.status === 422) {
            throw new Error(`Invalid search parameters: ${response.statusText}`);
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.text();
      })
      .then((responseText) => {
        if (!responseText) return;
        
        this.renderSearchResults(responseText);
        
        if (this.cachedMap.size >= this.MAX_CACHE_SIZE) {
          const firstKey = this.cachedMap.keys().next().value;
          this.cachedMap.delete(firstKey);
        }
        
        this.cachedMap.set(url, responseText);
        this.removeAttribute('error');
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Request was aborted');
          return;
        }
        
        console.error('Error fetching search data:', error);
        
        const errorMessage = error.message || 'Failed to load search results';
        this.setAttribute('error', errorMessage);
        
        if (this.searchContent) {
          const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
          const targetElement = document.getElementById(id);
          
          if (targetElement) {
            targetElement.innerHTML = `
              <div class="search-error">
                <p>${this.dataset.errorMessage || 'Search temporarily unavailable. Please try again.'}</p>
                <button type="button" class="retry-search">Retry</button>
              </div>
            `;
            
            const retryButton = targetElement.querySelector('.retry-search');
            if (retryButton) {
              retryButton.addEventListener('click', () => {
                this.onChange();
              });
            }
            
            this.searchContent.classList.remove('hidden');
          }
        }
      })
      .finally(() => {
        if (!isRetrying) {
          this.removeAttribute('loading');
          this.resetButton?.classList.remove('btn--loading');
          if (!this.hasAttribute('error')) {
            this.setAttribute('results', 'true');
          }
        }
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }

  handleSubmit(event) {
    const query = this.getQuery();
    const normalizedQuery = this.normalizeSKU(query);
    
    if (normalizedQuery !== query && normalizedQuery.length > 0) {
      const input = this.input;
      if (input) {
        input.value = normalizedQuery;
        
        if (this.dataset.showNormalization === 'true') {
          input.classList.add('normalized');
          if (this._normalizationTimeout) {
            clearTimeout(this._normalizationTimeout);
          }
          this._normalizationTimeout = setTimeout(() => {
            input.classList.remove('normalized');
            this._normalizationTimeout = null;
          }, 1000);
        }
      }
    }
  }

  disconnectedCallback() {
    if (this.handleClickOutsideBound) {
      document.removeEventListener('click', this.handleClickOutsideBound);
    }
    
    const input = this.input;
    const resetButton = this.resetButton;
    
    if (input && this._boundHandlers) {
      input.removeEventListener('input', this._boundHandlers.onChange);
      input.removeEventListener('focus', this._boundHandlers.onFocus);
    }
    
    if (resetButton && this._boundHandlers) {
      resetButton.removeEventListener('click', this._boundHandlers.clear);
    }
    
    if (this._boundHandlers) {
      this.removeEventListener('submit', this._boundHandlers.handleSubmit);
    }
    
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    
    if (this._retryTimeout) {
      clearTimeout(this._retryTimeout);
      this._retryTimeout = null;
    }
    
    if (this._normalizationTimeout) {
      clearTimeout(this._normalizationTimeout);
      this._normalizationTimeout = null;
    }
    
    if (this.cachedMap) {
      this.cachedMap.clear();
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.input;
  }
  
  show(activeElement = null, animate = true) {
    const wasOpen = this.open;
    this._focusSetEarly = false;
    
    const input = this.input;
    const isMobile = FoxTheme.config?.isTouch || 
                     FoxTheme.config?.mqlMobile || 
                     /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
    
    if (!wasOpen && input && isMobile) {
      this._focusSetEarly = true;
      const result = super.show(activeElement, animate);
      
      if (this.open && input) {
        try {
          input.focus();
        } catch (e) {
          setTimeout(() => {
            if (input && this.open) {
              try {
                input.focus();
              } catch (e2) {
                console.warn('Could not focus input on mobile device:', e2);
              }
            }
          }, 50);
        }
      }
      
      return result;
    }
    
    const result = super.show(activeElement, animate);
    
    if (!wasOpen && input && !isMobile) {
      this._focusSetEarly = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (input && this.open) {
            try {
              input.focus();
            } catch (e) {
              setTimeout(() => input && this.open && input.focus(), 50);
            }
          }
        });
      });
    }
    return result;
  }
  
  _setupFocusTrap(first, last, drawer) {
    FoxTheme.a11y.removeTrapFocus();
    this._keydownListenerAttached = false;
    
    FoxTheme.a11y.trapFocusHandlers.focusin = (event) => {
      if (drawer.contains(event.target) && !this._keydownListenerAttached) {
        document.addEventListener('keydown', FoxTheme.a11y.trapFocusHandlers.keydown);
        this._keydownListenerAttached = true;
      }
    };
    
    FoxTheme.a11y.trapFocusHandlers.focusout = () => {
      if (this._keydownListenerAttached) {
        document.removeEventListener('keydown', FoxTheme.a11y.trapFocusHandlers.keydown);
        this._keydownListenerAttached = false;
      }
    };
    
    FoxTheme.a11y.trapFocusHandlers.keydown = function (event) {
      if (event.code.toUpperCase() !== 'TAB') return;
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      } else if ((event.target === drawer || event.target === first) && event.shiftKey) {
        event.preventDefault();
        last.focus();
      }
    };
    
    if (this.input && document.activeElement === this.input) {
      this._keydownListenerAttached = true;
      document.addEventListener('keydown', FoxTheme.a11y.trapFocusHandlers.keydown);
    }
    
    document.addEventListener('focusout', FoxTheme.a11y.trapFocusHandlers.focusout);
    document.addEventListener('focusin', FoxTheme.a11y.trapFocusHandlers.focusin);
  }
  
  _findModalComponent() {
    if (typeof clearModalComponentCount === 'undefined') return null;
    let currentClass = this.constructor;
    for (let i = 0; i < 10 && currentClass; i++) {
      if (clearModalComponentCount.has(currentClass)) return currentClass;
      currentClass = Object.getPrototypeOf(currentClass);
    }
    return null;
  }
  
  _incrementLockCount(ModalComponent) {
    if (typeof clearModalComponentCount === 'undefined') return false;
    try {
      const currentLockCount = (clearModalComponentCount.get(ModalComponent) || 0) + 1;
      clearModalComponentCount.set(ModalComponent, currentLockCount);
      return true;
    } catch (e) {
      console.error('Error incrementing modal lock count:', e);
      return false;
    }
  }
  
  handleAfterShow() {
    const input = this.input;
    const focusAlreadySet = this._focusSetEarly || (input && document.activeElement === input);

    if (focusAlreadySet) {
      const elements = FoxTheme.a11y.getFocusableElements(this);
      const [first, last] = [elements[0], elements[elements.length - 1]];
      
      if (!first || !last) {
        super.handleAfterShow();
        return;
      }
      
      this._setupFocusTrap(first, last, this);
      
      const ModalComponent = this._findModalComponent();
      
      if (this.isLockingNeeded && ModalComponent) {
        if (this._incrementLockCount(ModalComponent)) {
          document.body.classList.remove(this.classes.showing);
          document.body.classList.add(this.classes.show);
        } else {
          super.handleAfterShow();
          return;
        }
      } else if (this.isLockingNeeded) {
        super.handleAfterShow();
        return;
      }
    } else {
      super.handleAfterShow();
    }
    this._focusSetEarly = false;
  }
}
customElements.define('search-drawer', SearchDrawer);
